import { Button, message, Modal, Select } from 'antd';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import { EditInfos, Infos } from '../components/editinfos';
import { LayoutSite } from '../components/layout';
import { Loader } from '../components/loader';
import { get_race, get_races, removerace, updaterace } from '../services/api.service';
import { isViewer } from '../services/auth.header';
import { formatdateYYYYMMDD, MLCompetitor, MLRace } from '../types';
import './race.scss';

/* -------------------------------------------------------------------------------- *\
|                               RacePage
\* -------------------------------------------------------------------------------- */
export const RacePage: FC<{ children?: ReactNode }> = ({ children }) =>
{
    const [loading, setLoading] = useState(true);
    const [mustload, setMustload] = useState(true);
    const [ismodified, setModified] = useState(false);
    const viewer = isViewer();
    const [originalrace, setOriginalRace] = useState<MLRace | null>(null);
    const [currentrace, setCurrentRace] = useState<MLRace | null>(null);
    const [currentraces, setCurrentRaces] = useState<MLRace[]>([]);
    
    const navigate = useNavigate();

    const [editedrace, setEditedRace] = useState<MLRace | null>(null);
    const [dlgracevisible, setDlgracevisible] = useState(false);

    const [editedcompetitor, setEditedCompetitor] = useState<MLCompetitor | null>(null);
    const [dlgcompetitorvisible, setDlgcompetitorvisible] = useState(false);
    const [dlgcompetitortimeerror, setDlgcompetitorerror] = useState(false);
    const [competitorstatuscustom, setCompetitorStatusCustom] = useState(false);

    const COMPETITOR_STATUSES = ['DP', 'NP', 'AI', 'TB'];

    const lifInputRef = useRef<HTMLInputElement>(null);

    const params = useParams();
    const date: string = params.date!;
    const track: string = params.track!;
    const raceno: number = Number(params.raceno ? params.raceno : "0");

    /* -------------------------------------------------------------------------------- *\
    |                               computedatetime
    \* -------------------------------------------------------------------------------- */
    function computedatetime(r: MLRace): [string, string]
    {
        var day: string = "";
        var time: string = "";
    
        if (r && r.date)
        {
            const d = new Date(r.date);
            if (!isNaN(d.getTime()))
            {
                day = d.toLocaleDateString();
                time = d.toLocaleTimeString().substring(0, 5);
            }
        }
        return [day, time];
    }
    /* -------------------------------------------------------------------------------- *\
    |                               getRaces
    \* -------------------------------------------------------------------------------- */
    const getRaces = async (currentdate: string, track: string) =>
    {
        get_races(currentdate, track)
            .then((races: MLRace[]) =>
            {
                setCurrentRaces(races);
            })
            .catch(error =>
            {
                message.error(error.message);
            });
    }
    /* -------------------------------------------------------------------------------- *\
    |                               getRaceInfos
    \* -------------------------------------------------------------------------------- */
    const getRaceInfos = async (currentdate: string, track: string, raceno: number) =>
    {
        if (!mustload) return;
        setMustload(false);

        setLoading(true);
        get_race(currentdate, track, raceno)
            .then((r: MLRace |null) =>
            {
                if (r)
                {
                    const [day, time] = computedatetime(r);
                    r.day = day;
                    r.time = time;
                    setOriginalRace(JSON.parse(JSON.stringify(r)));
                    setCurrentRace(r);

                    getRaces(currentdate, track);
                }
                else
                {
                    message.error("unable to find race infos " + currentdate + " " + track + " " + raceno.toString());
                }
                setLoading(false);
            })
            .catch(error =>
            {
                message.error(error.message);
                setLoading(false);
                if (error.status === 403 || error.status === 401) navigate('/login');
            });
    }

    /* -------------------------------------------------------------------------------- *\
    |                               askremoverace
    \* -------------------------------------------------------------------------------- */
    const askremoverace = async () =>
    {
        if (currentrace && window.confirm("Do you really want to remove this race ?"))
        {
            removerace(currentrace).then((result: boolean) =>
            {
                if (result)
                {
                    message.info("race removed");
                    navigate("/");
                }
                else
                {
                    message.info("an error occured and race is not removed");
                }
            })
            .catch(error =>
            {
                message.error(error.message);
                if (error.status === 403 || error.status === 401) navigate('/login');
            });
            }
    }
    /* -------------------------------------------------------------------------------- *\
    |                               updaterace
    \* -------------------------------------------------------------------------------- */
    const askupdaterace = async () =>
    {
        if (currentrace && window.confirm("Do you really want to update this race ?"))
        {
            updaterace(currentrace).then((result: boolean) =>
            {
                if (result)
                {
                    message.info("race updated");
                    setMustload(true);
                }
                else
                {
                    message.info("an error occured and race is not removed");
                }
            })
            .catch(error =>
            {
                message.error(error.message);
                if (error.status === 403 || error.status === 401) navigate('/login');
            });
        }
    }
    /* -------------------------------------------------------------------------------- *\
    |                               
    \* -------------------------------------------------------------------------------- */
    function editrace()
    {
        if (currentrace)
        {
            const r: MLRace = Object.assign(new MLRace(), currentrace);
            setEditedRace(r);
            setDlgracevisible(true);
        }
    }
    /* -------------------------------------------------------------------------------- *\
    |                               dlgraceclose
    \* -------------------------------------------------------------------------------- */
    function dlgraceclose(save: boolean)
    {
        setDlgracevisible(false);
        if (save && editedrace)
        {
            // the edited "time" is only a display field: fold it back into the authoritative "date"
            if (editedrace.date && editedrace.time)
            {
                const d = new Date(editedrace.date);
                const parts = editedrace.time.split(':');
                const hh = Number(parts[0]);
                const mm = Number(parts[1]);
                if (!isNaN(d.getTime()) && !isNaN(hh) && !isNaN(mm))
                {
                    d.setHours(hh, mm, 0, 0);
                    editedrace.date = d.toISOString();
                    const [day, time] = computedatetime(editedrace);
                    editedrace.day = day;
                    editedrace.time = time;
                }
            }
            setCurrentRace(editedrace);
        }
    }
    /* -------------------------------------------------------------------------------- *\
    |                               editcompetitor
    \* -------------------------------------------------------------------------------- */
    function editcompetitor(competitor: MLCompetitor)
    {
        const c: MLCompetitor = Object.assign(new MLCompetitor(), competitor);
        setEditedCompetitor(c);
        setDlgcompetitorerror(false);
        setCompetitorStatusCustom(!!c.status && !COMPETITOR_STATUSES.includes(c.status));
        setDlgcompetitorvisible(true);
    }
    /* -------------------------------------------------------------------------------- *\
    |                               dlgcompetitorclose
    \* -------------------------------------------------------------------------------- */
    function dlgcompetitorclose(save: boolean)
    {
        if (save && dlgcompetitortimeerror)
        {
            alert("Correct the error before validate the changes");
            return;
        }
        setDlgcompetitorvisible(false);
        if (save && currentrace && editedcompetitor)
        {
            for (var i = 0; i < currentrace.competitors.length; i++)
            {
                if (currentrace.competitors[i].number === editedcompetitor.number)
                {
                    currentrace.competitors[i].name = editedcompetitor.name;
                    currentrace.competitors[i].status = editedcompetitor.status;
                    currentrace.competitors[i].draw = editedcompetitor.draw;
                    currentrace.competitors[i].distance = editedcompetitor.distance;
                    currentrace.competitors[i].driver = editedcompetitor.driver;
                    currentrace.competitors[i].trainer = editedcompetitor.trainer;
                    currentrace.competitors[i].owner = editedcompetitor.owner;
                    currentrace.competitors[i].rank = editedcompetitor.rank;
                    currentrace.competitors[i].time = editedcompetitor.time;

                    const r1 = JSON.stringify(originalrace);
                    const r2 = JSON.stringify(currentrace);
                    setModified(r1 !== r2);
                }
            }
        }
    }
    /* -------------------------------------------------------------------------------- *\
    |                               lif import
    \* -------------------------------------------------------------------------------- */
    function lifTimeToRaceTime(lifTime: string): string
    {
        if (!lifTime) return '';
        let totalSeconds: number;
        if (lifTime.includes(':'))
        {
            // format m:ss.d  (ex: "1:11.1")
            const [minPart, secPart] = lifTime.split(':');
            const mins = parseInt(minPart);
            const secs = parseFloat(secPart);
            if (isNaN(mins) || isNaN(secs)) return '';
            totalSeconds = mins * 60 + secs;
        }
        else
        {
            // format s.mmm  (ex: "13.461")
            totalSeconds = parseFloat(lifTime);
            if (isNaN(totalSeconds)) return '';
        }
        const mins = Math.floor(totalSeconds / 60);
        const secs = Math.floor(totalSeconds % 60);
        const ms = Math.round((totalSeconds - Math.floor(totalSeconds)) * 1000);
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}:${String(ms).padStart(3, '0')}`;
    }

    function handleLif(file: File)
    {
        const reader = new FileReader();
        reader.onload = (e) =>
        {
            const text = e.target?.result as string;
            const lines = text.split('\n').slice(1);

            const entries: { number: number; rank: number; name: string; time: string }[] = [];
            const unranked: { number: number; rankStr: string; name: string }[] = [];
            for (const line of lines)
            {
                const cols = line.split(',');
                if (cols.length < 4) continue;
                const rankStr = cols[0].trim();
                if (!rankStr) continue;
                const horseNumber = parseInt(cols[1].trim());
                if (isNaN(horseNumber)) continue;
                const name = cols[3].trim();
                const rank = parseInt(rankStr);
                if (isNaN(rank))
                {
                    unranked.push({ number: horseNumber, rankStr, name });
                }
                else
                {
                    const time = lifTimeToRaceTime(cols[6]?.trim() ?? '');
                    entries.push({ number: horseNumber, rank, name, time });
                }
            }

            if (entries.length === 0) { message.error('No valid entries found in LIF file'); return; }

            Modal.confirm({
                title: <div style={{ color: 'white', borderBottom: '1px solid #fff', paddingBottom: 10 }}>Import LIF</div>,
                icon: null,
                content: (
                    <>
                        <style>{`.lif-row:hover { background: #2a2a2a; border-radius: 3px; }`}</style>
                        <div style={{ marginTop: 8 }}>
                            {entries.map(e => (
                                <div key={e.number} className="lif-row" style={{ display: 'grid', gridTemplateColumns: '30px 40px 1fr 90px', gap: '0 12px', padding: '3px 6px', color: 'white' }}>
                                    <span>{e.rank}</span>
                                    <span>#{e.number}</span>
                                    <span>{e.name}</span>
                                    <span style={{ fontFamily: 'monospace' }}>{e.time}</span>
                                </div>
                            ))}
                            {unranked.length > 0 && <>
                                <div style={{ borderTop: '1px solid #333', margin: '8px 0' }} />
                                {unranked.map(e => (
                                    <div key={e.number} className="lif-row" style={{ display: 'grid', gridTemplateColumns: '30px 40px 1fr', gap: '0 12px', padding: '3px 6px', color: '#888' }}>
                                        <span>{e.rankStr}</span>
                                        <span>#{e.number}</span>
                                        <span>{e.name}</span>
                                    </div>
                                ))}
                            </>}
                        </div>
                    </>
                ),
                okText: 'Apply',
                cancelText: 'Cancel',
                onOk: () =>
                {
                    if (!currentrace) return;
                    const newRace: MLRace = JSON.parse(JSON.stringify(currentrace));
                    for (const entry of entries)
                    {
                        const c = newRace.competitors.find((c: MLCompetitor) => c.number === entry.number);
                        if (c) { c.rank = entry.rank; c.time = entry.time; }
                    }
                    setCurrentRace(newRace);
                },
            });
        };
        reader.readAsText(file);
    }

    /* -------------------------------------------------------------------------------- *\
    |                               exportracing
    \* -------------------------------------------------------------------------------- */
    function exportracing()
    {
        if (!currentrace) return;

        const meetingdate = new Date(currentrace.meetingdate ? currentrace.meetingdate : currentrace.date);
        const dateStr = `${String(meetingdate.getDate()).padStart(2, '0')}/${String(meetingdate.getMonth() + 1).padStart(2, '0')}/${meetingdate.getFullYear()}`;

        const racesToExport = currentraces.map(r => r.number === currentrace.number ? currentrace : r);

        const racing = {
            Date: dateStr,
            IDHippo: currentrace.sic,
            LibHippo: currentrace.trackname,
            numR: currentraces.length,
            courses: racesToExport.map(r =>
            {
                const [, rtime] = computedatetime(r);
                return {
                    NumC: r.number,
                    HeureC: rtime,
                    LibC: r.name || '',
                    codeParcours: r.trackcode || '',
                    NDPart: r.competitors?.length || 0,
                    Dist: r.distance || 0,
                    Disc: r.type || '',
                    partants: (r.competitors || []).map((c: MLCompetitor) => ({
                        Num: c.number,
                        EtatP: c.status || '',
                        DistP: c.distance || 0,
                        NomC: c.name || '',
                        Jock: c.driver || '',
                        Entr: c.trainer || '',
                        Prop: c.owner || ''
                    }))
                };
            })
        };

        const blob = new Blob([JSON.stringify(racing, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${date}${currentrace.sic}_racing.json`;
        a.click();
        URL.revokeObjectURL(url);
    }
    /* -------------------------------------------------------------------------------- *\
    |                               exportjson
    \* -------------------------------------------------------------------------------- */
    function exportjson()
    {
        if (!currentrace) return;
        const blob = new Blob([JSON.stringify(currentrace, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${date}${currentrace.sic}${currentrace.number}.json`;
        a.click();
        URL.revokeObjectURL(url);
    }
    /* -------------------------------------------------------------------------------- *\
    |                               effects
    \* -------------------------------------------------------------------------------- */
    useEffect(() =>
    {
        getRaceInfos(date, track, raceno);
        // eslint-disable-next-line
    }, [date, track, raceno, mustload])

    useEffect(() =>
    {
        const r1 = JSON.stringify(originalrace);
        const r2 = JSON.stringify(currentrace);
        setModified(r1 !== r2);
    }, [originalrace, currentrace]);

    return (
        <LayoutSite>
            <div className="race">
                {loading ? <Loader /> : <></>}
                <div className="race__header">
                    <div></div>
                </div>
                <div className="race__body">
                    <div className="race__body__races">
                    {
                        currentraces.map((race: MLRace, index: number) =>
                        {
                            const meetingdate = new Date(race.meetingdate ? race.meetingdate : race.date);
                            return <div className={"race__body__race" + (race.number === currentrace?.number ? " race__body__raceactive" : "")} key={'race_' + index} 
                                onClick={() => window.location.href = '/race/' + formatdateYYYYMMDD(meetingdate) + '/' + race.sic + '/' + race.number}>
                                {race.number}
                            </div>
                        })
                    }
                    </div>
                    <Infos label="date" value={currentrace?.day} />
                    <Infos label="time" value={currentrace?.time} />
                    <Infos label="number" value={currentrace?.number} />
                    <Infos label="name" value={currentrace?.name} />
                    <Infos label="distance" value={currentrace?.distance ? currentrace.distance + "m" : ""} />
                    <Infos label="trackcode" value={currentrace?.sic} />
                    <Infos label="state" value={currentrace?.state} />
                    <Infos label="track" value={currentrace?.trackname} />
                    <Infos label="code" value={currentrace?.trackcode} />
                    <Infos label="type" value={currentrace?.type} />
                    <Infos label="source" value={currentrace?.source} />
                    <Infos label="runners" value={currentrace?.competitors?.length} />
                    {
                        currentrace?.comment && <Infos label="comment" value={currentrace.comment} />
                    }
                    <Infos label="meetingdate" value={currentrace?.meetingdate} />

                    <div className="race__labelvalue race__labelvalue__buttons">
                        <div className="race__labelvalue__label"></div>
                        <div className="race__labelvalue__value">
                            {!viewer && <Button size='small' onClick={() => editrace()}>Edit</Button>}
                            {!viewer && <Button size='small' onClick={() => lifInputRef.current?.click()}>Import LIF</Button>}
                            <Button size='small' onClick={() => exportjson()}>Export</Button>
                            <Button size='small' style={{ width: 120 }} onClick={() => exportracing()}>Export for Racing</Button>
                            <input
                                ref={lifInputRef}
                                type="file"
                                accept=".lif"
                                style={{ display: 'none' }}
                                onChange={e => { const f = e.target.files?.[0]; if (f) { handleLif(f); e.target.value = ''; } }}
                            />
                            {!viewer && <Button size='small' style={{ marginLeft: 20 }} onClick={() => askremoverace()}>Remove</Button>}
                            {
                                !viewer && ismodified &&
                                <Button size='small' className='race__validate' onClick={() => askupdaterace()}>Update</Button>
                            }
                        </div>
                    </div>
                    
                    <div className='race__body__competitors'>
                        <div className='race__body__competitors__header'>
                            <div className='race__body__competitors__line__element race__body__competitors__line__small'>number</div>
                            <div className='race__body__competitors__line__element'>name</div>
                            <div className='race__body__competitors__line__element race__body__competitors__line__small'>status</div>
                            <div className='race__body__competitors__line__element race__body__competitors__line__small'>distance</div>
                            <div className='race__body__competitors__line__element'>driver</div>
                            <div className='race__body__competitors__line__element'>trainer</div>
                            <div className='race__body__competitors__line__element'>owner</div>
                            <div className='race__body__competitors__line__element race__body__competitors__line__small'>draw</div>
                            <div className='race__body__competitors__line__element race__body__competitors__line__small'>rank</div>
                            <div className='race__body__competitors__line__element race__body__competitors__line__time'>time</div>
                            <div className='race__body__competitors__line__element race__body__competitors__line__button'></div>
                        </div>
                        {
                        currentrace?.competitors.map((c: MLCompetitor, ic: number) => 
                            {
                                return (
                                    <div className='race__body__competitors__line' key={'competitor_' + ic}>
                                        <div className='race__body__competitors__line__element race__body__competitors__line__small'>{c.number}</div>
                                        <div className='race__body__competitors__line__element race__body__competitors__line__name'><div>{c.name}</div>
                                            {
                                                c.urlsilk && 
                                                <div>
                                                    <img src={c.urlsilk} data-tooltip-id={"tooltip-silk-" + ic} alt="silk"/>
                                                    <Tooltip id={"tooltip-silk-" + ic} className="race__body__competitors__line__silk">
                                                        <div>
                                                            <img src={c.urlsilk} alt="silk"  style={{ height: '200px' }}/>
                                                        </div>
                                                    </Tooltip>
                                                </div>
                                            }
                                        </div>
                                        <div className='race__body__competitors__line__element race__body__competitors__line__small'>{c.status}</div>
                                        <div className='race__body__competitors__line__element race__body__competitors__line__small'>{c.distance}</div>
                                        <div className='race__body__competitors__line__element'>{c.driver}</div>
                                        <div className='race__body__competitors__line__element'>{c.trainer}</div>
                                        <div className='race__body__competitors__line__element'>{c.owner}</div>
                                        <div className='race__body__competitors__line__element race__body__competitors__line__small'>{c.draw}</div>
                                        <div className='race__body__competitors__line__element race__body__competitors__line__small'>{c.rank ? c.rank : ''}</div>
                                        <div className='race__body__competitors__line__element race__body__competitors__line__time'>{c.time}</div>
                                        <div className='race__body__competitors__line__element race__body__competitors__line__button'>{!viewer && <Button size='small' onClick={() => editcompetitor(c)}>Edit</Button>}</div>
                                    </div>
                                );
                            })
                        }
                    </div>
                </div>

                {
                    dlgracevisible &&
                    <Modal className='race__modal' title={"Edit race (" + editedrace?.day + ")"} open={dlgracevisible} onOk={() => dlgraceclose(true)} onCancel={() => dlgraceclose(false)}>
                        <EditInfos type="time" label="time" value={editedrace?.time} onChange={(v) => { if (editedrace) editedrace.time = v as string }} />
                        <EditInfos type="string" label="name" value={editedrace?.name} onChange={(v) => { if (editedrace) editedrace.name = v as string}} />
                        <EditInfos type="string" label="code" value={editedrace?.trackcode} onChange={(v) => { if (editedrace) editedrace.trackcode = v as string}} />
                        <EditInfos type="select" label="type" value={editedrace?.type} options={[
                            { value: 'ATTELÉ', label: 'ATTELÉ' },
                            { value: 'CROSS', label: 'CROSS' },
                            { value: 'HAIES', label: 'HAIES' },
                            { value: 'MONTÉ', label: 'MONTÉ' },
                            { value: 'PLAT', label: 'PLAT' },
                            { value: 'STEEPLE', label: 'STEEPLE' },
                        ]} onChange={(v) => { if (editedrace) editedrace.type = v as string}} />
                        <EditInfos type="string" label="source" value={editedrace?.source} onChange={(v) => { if (editedrace) editedrace.source = v as string}} />
                        <EditInfos type="string" label="trackname" value={editedrace?.trackname} onChange={(v) => { if (editedrace) editedrace.trackname = v as string}} />
                    </Modal>
                }
                {
                    dlgcompetitorvisible &&
                    <Modal className='race__modal' title={"Edit competitor"} open={dlgcompetitorvisible} onOk={() => dlgcompetitorclose(true)} onCancel={() => dlgcompetitorclose(false)}>
                        <EditInfos type="string" label="name" value={editedcompetitor?.name} onChange={(v) => { if (editedcompetitor) editedcompetitor.name = v as string}} />
                        <div className="race__editvalue">
                            <div className="race__editvalue__label">status</div>
                            <div className="race__editvalue__value" style={{ display: 'flex', gap: 8 }}>
                                <Select
                                    style={{ width: 120 }}
                                    value={competitorstatuscustom ? 'custom' : (editedcompetitor?.status || '')}
                                    onChange={(v) => {
                                        if (v === 'custom') {
                                            setCompetitorStatusCustom(true);
                                        } else {
                                            setCompetitorStatusCustom(false);
                                            if (editedcompetitor) editedcompetitor.status = v;
                                        }
                                    }}
                                    options={[
                                        ...COMPETITOR_STATUSES.map(s => ({ value: s, label: s === 'NP' ? 'NP (SCR)' : s })),
                                        { value: 'custom', label: 'custom' },
                                    ]}
                                />
                                {competitorstatuscustom &&
                                    <input
                                        className="ant-input race__editvalue__value"
                                        defaultValue={editedcompetitor?.status || ''}
                                        onChange={(e) => { if (editedcompetitor) editedcompetitor.status = e.target.value; }}
                                        style={{ width: 120 }}
                                    />
                                }
                            </div>
                        </div>
                        <EditInfos type="number" label="draw" value={editedcompetitor && editedcompetitor.draw ? editedcompetitor.draw : 0} onChange={(v) => { if (editedcompetitor) editedcompetitor.draw = v as number}} />
                        <EditInfos type="number" label="distance" value={editedcompetitor?.distance} onChange={(v) => { if (editedcompetitor) editedcompetitor.distance = v as number}} />
                        <EditInfos type="string" label="jockey/driver" value={editedcompetitor?.driver} onChange={(v) => { if (editedcompetitor) editedcompetitor.driver = v as string}} />
                        <EditInfos type="string" label="trainer" value={editedcompetitor?.trainer} onChange={(v) => { if (editedcompetitor) editedcompetitor.trainer = v as string}} />
                        <EditInfos type="string" label="owner" value={editedcompetitor?.owner} onChange={(v) => { if (editedcompetitor) editedcompetitor.owner = v as string}} />
                        <EditInfos type="number" label="rank" value={editedcompetitor?.rank} onChange={(v) => { if (editedcompetitor) editedcompetitor.rank = v as number}} />
                        <EditInfos type="string" label="time" value={editedcompetitor?.time} onChange={(vv: string | number | FileList) =>
                            {
                                const v: string = vv as string;
                                if (editedcompetitor)
                                {
                                    var ok: boolean = true;
                                    if (v && v.length > 0)
                                    {
                                        ok = ok && (v.length === 9);
                                        ok = ok && (v.split(':')
                                            .filter((n, i) => n.length === (i === 2 ? 3 : 2))
                                            .filter((n) => !isNaN(Number(n)))
                                            .filter((n,i) => (i !== 2 ? Number(n) < 60 : 1000))
                                            .length === 3);
                                    }

                                    setDlgcompetitorerror(!ok);
                                    if (ok)
                                    {
                                        editedcompetitor.time = v;
                                    }
                                    
                                }
                            }
                        }
                        />
                        {
                            dlgcompetitortimeerror && <div className='race__modal__error'>Time should be 00:00:000</div>
                        }
                    </Modal>
                }
            </div>
        </LayoutSite>
    );
}
